import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  {
    number: '01',
    title: 'Entre no Site',
    description: 'Acesse averixa.com.br. Nenhuma instalação, conta ou cadastro necessário.',
    color: '#7a00ff',
    state: 'Disponível',
  },
  {
    number: '02',
    title: 'Abra o Chat',
    description: 'Vá até a seção Chat. Digite sua mensagem em linguagem natural — pergunta, código, texto.',
    color: '#00f0ff',
    state: 'Disponível',
  },
  {
    number: '03',
    title: 'Receba Resposta',
    description: 'O sistema responde via modo demo (respostas contextuais locais). Integração com API em ativação.',
    color: '#ff00c8',
    state: 'Modo Demo',
  },
  {
    number: '04',
    title: 'Explore o World',
    description: 'Conheça a arquitetura modular — Aurora, Kaira e Averexa. Ambiente interativo em preview.',
    color: '#00ff9c',
    state: 'Preview',
  },
  {
    number: '05',
    title: 'Multimodal (Em breve)',
    description: 'Estrutura preparada para imagem, áudio e vídeo. Módulos sendo integrados progressivamente.',
    color: '#7a00ff',
    state: 'Em desenvolvimento',
  },
];

export default function ComoUsar() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.fromTo('.como-header', { opacity: 0, y: 30 }, {
      opacity: 1, y: 0, duration: 1, ease: 'power3.out',
      scrollTrigger: { trigger: section, start: 'top 70%', toggleActions: 'play none none none' },
    });

    gsap.fromTo('.como-step', { opacity: 0, x: -30 }, {
      opacity: 1, x: 0, duration: 0.7, stagger: 0.15, ease: 'power3.out',
      scrollTrigger: { trigger: section, start: 'top 55%', toggleActions: 'play none none none' },
    });
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full py-32 px-6"
      style={{ background: '#0a0a0e', zIndex: 5 }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="como-header text-center mb-20">
          <span className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-4 block" style={{ color: '#00ff9c' }}>
            Fluxo de Uso
          </span>
          <h2 className="font-syncopate text-white uppercase" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', textShadow: '0 0 40px rgba(0,255,156,0.15)' }}>
            Como Usar a Vérixa
          </h2>
          <p className="mt-4 text-slate-400 max-w-lg mx-auto">
            O que funciona agora, o que está em teste e o que está sendo construído.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-5 top-0 bottom-0 w-px hidden sm:block" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,240,255,0.2) 20%, rgba(0,240,255,0.2) 80%, transparent)' }} />

          <div className="space-y-8">
            {STEPS.map((step) => (
              <div key={step.number} className="como-step relative flex gap-6 sm:gap-10">
                {/* Number circle */}
                <div
                  className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center z-10"
                  style={{
                    background: `${step.color}15`,
                    border: `1px solid ${step.color}40`,
                    boxShadow: `0 0 20px ${step.color}20`,
                  }}
                >
                  <span className="text-[11px] font-bold" style={{ color: step.color }}>{step.number}</span>
                </div>

                {/* Content */}
                <div
                  className="flex-1 p-6 rounded-panel mb-1"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="font-syncopate text-sm text-white uppercase tracking-[0.06em]">
                      {step.title}
                    </h3>
                    <span
                      className="flex-shrink-0 text-[9px] uppercase tracking-[0.1em] px-2 py-1 rounded-pill"
                      style={{
                        background: `${step.color}15`,
                        color: step.color,
                        border: `1px solid ${step.color}30`,
                      }}
                    >
                      {step.state}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
